import React, { useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { CircularProgress, Typography } from '@mui/material'

import Setup from './setup'
import WizardContainer from '../shared/WizardContainer'
import WizardFooter from '../shared/WizardFooter'
import FieldOptions from './field-options'
import Info from './info'
import useCustomFields from '../../hooks/api/useCustomFields'
import useAlert from '../../hooks/useAlert'

const CustomFieldsSetup = () => {
  const {
    navigateToNext,
    companyConfig,
    setCompanyConfig,
    updateCompanyConfig,
    wizardState,
    updateWizardState,
  } = useOutletContext()

  const { customFields: fieldState = [], customFieldsStep: step = 0 } =
    wizardState

  const hasInitializedData = useMemo(() => !!wizardState.customFields, [])

  const goToNextStep = () => updateWizardState({ customFieldsStep: step + 1 })
  const goToPrevStep = () => updateWizardState({ customFieldsStep: step - 1 })

  const {
    customFields: customFieldsData,
    setCustomFields,
    saveCustomFields,
    types,
    isLoading,
  } = useCustomFields()

  useEffect(() => {
    if (isLoading) return
    if (hasInitializedData) return setCustomFields(fieldState)

    updateWizardState({ customFields: customFieldsData })
  }, [isLoading])

  const setFieldState = (val) => {
    const customFields = typeof val === 'function' ? val(fieldState) : val

    setCustomFields(customFields)
    updateWizardState({ customFields })
  }

  const getIsValid = () => {
    if (!types.length || !fieldState.length) return false

    return fieldState.every((f) => {
      const type = types.find((t) => t.id === f.typeId)

      const isValid =
        !!f.name &&
        (type.dataType !== 'dropdown' || f.options.some((o) => !!o.name))

      return isValid
    })
  }

  const { createAlert } = useAlert()

  const isValid = getIsValid()

  const submit = () => {
    const newConfig = {
      ...companyConfig,
      hasSavedCustomFields: true,
    }

    return updateCompanyConfig(newConfig)
      .then(saveCustomFields)
      .then(() => {
        navigateToNext()
      })
      .catch((e) => {
        createAlert({
          severity: 'error',
          message: 'Something went wrong',
          action: {
            label: 'Skip this step',
            onClick: () => navigateToNext(),
          },
        })
      })
  }

  const steps = useMemo(
    () => [
      {
        primaryButton: {
          label: 'Get started',
          onClick: goToNextStep,
        },
        skipButton: {
          label: "No thanks I'll come back later",
          onClick: navigateToNext,
        },
      },
      {
        primaryButton: {
          label: 'Next',
          onClick: goToNextStep,
        },
        hideSkipButton: true,
        backButton: {
          onClick: goToPrevStep,
        },
      },
      {
        primaryButton: {
          label: 'Save Custom Fields',
          disabled: !fieldState.length || !isValid,
          onClick: submit,
        },
        skipButton: {
          onClick: navigateToNext,
        },
        backButton: {
          onClick: goToPrevStep,
        },
      },
    ],
    [fieldState, isValid, step, customFieldsData]
  )

  const { primaryButton, skipButton, hideSkipButton, backButton } = steps[step]

  if (isLoading) return <CircularProgress />

  return (
    <WizardContainer>
      <Typography variant={'h5'}>Custom fields</Typography>
      {step === 0 && <Info />}
      {step === 1 && <FieldOptions />}
      {step === 2 && (
        <Setup
          fieldState={fieldState}
          setFieldState={setFieldState}
          types={types}
        />
      )}

      <WizardFooter
        skipButton={skipButton}
        primaryButton={primaryButton}
        hideSkipButton={hideSkipButton}
        backButton={backButton}
      />
    </WizardContainer>
  )
}

export default CustomFieldsSetup
